/** Every string and constant the Learning Catalog shows, in one place (the detail popups' figures live in catalogStatsText.js
 * and are re-exported here). */
export { STATS_TEXT, STORED_MATCH_TEXT, TRACK_TEXT } from "./catalogStatsText";

export const CATALOG_STATE = Object.freeze({ LEARNED: "learned", UNDONE: "undone", ALL: "all" });

// The catalog source the Manual view lists (the catalog API's `source`).
export const CATALOG_SOURCE = Object.freeze({ MANUAL: "manual" });

// The page's two views: Conversations is the learning history (every learned case), Manual is the catalog of taught patterns.
// The page opens on Conversations (what Arta learned on its own); no choice is remembered between visits.
export const CATALOG_VIEW = Object.freeze({ HISTORY: "history", MANUAL: "manual" });
export const DEFAULT_VIEW = CATALOG_VIEW.HISTORY;

export const VIEW_OPTIONS = Object.freeze([
  { key: CATALOG_VIEW.HISTORY, label: "Conversations" },
  { key: CATALOG_VIEW.MANUAL, label: "Manual" },
]);

export const STATE_OPTIONS = Object.freeze([
  { key: CATALOG_STATE.LEARNED, label: "Learned" },
  { key: CATALOG_STATE.UNDONE, label: "Undone" },
  { key: CATALOG_STATE.ALL, label: "All" },
]);

export const PAGE_SIZE = 20;
export const FILTER_DEBOUNCE_MS = 300;
export const FILTER_MAX_CHARS = 200;

export const FIX_KIND_LABELS = Object.freeze({ fix: "Fix", mitigation: "Mitigation", workaround: "Workaround" });

// Table cells show a short date and time; the detail popup the full local timestamp.
const SHORT_WHEN = Object.freeze({ dateStyle: "medium", timeStyle: "short" });
export const shortWhen = (iso) => (iso ? new Date(iso).toLocaleString([], SHORT_WHEN) : "");
export const fullWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export const CATALOG_TEXT = Object.freeze({
  title: "Learning Catalog",
  description: Object.freeze({
    [CATALOG_VIEW.HISTORY]: "Every case Arta learned from. If one was recorded by mistake, undo it. You can redo it any time.",
    [CATALOG_VIEW.MANUAL]: "Patterns and causes engineers taught. They are learned as soon as they are saved; undo one to stop using it.",
  }),
  view: "Learned from",
  refresh: "Refresh",
  loading: "Loading…",
  notConnected: "The learning catalog is not connected yet.",
  failed: "Could not load the learning catalog.",
  empty: "Nothing learned matches these filters.",
  table: {
    caption: "Patterns engineers taught, newest first",
    when: "Saved",
    category: "Product · category",
    rootCause: "Root cause",
    symptoms: "Symptoms",
    fixKind: "Fix type",
    state: "State",
    matchSummary: (reused, fresh) => `${reused} reused · ${fresh} new`,
    symptomSummary: (count, first) => (count > 1 ? `${first} (+${count - 1} more)` : first),
  },
  detail: {
    title: "Learned pattern",
    close: "Close",
    open: (label) => `Open details: ${label}`,
  },
  filters: {
    label: "Filter learnings",
    product: "Product",
    allProducts: "All products",
    category: "Category",
    allCategories: "All categories",
    option: (name, count) => `${name} (${count})`,
    problem: "Problem / root cause",
    problemHint: "Search the root cause",
    keyword: "Keyword",
    keywordHint: "Symptoms, steps, root cause or fix",
    symptom: "Symptom",
    symptomHint: "Search the symptoms",
    state: "Show",
    clear: "Clear filters",
  },
  card: {
    category: "Category",
    product: "Product",
    symptoms: "Symptoms",
    steps: "Troubleshooting steps",
    rootCause: "Root cause",
    fix: "What was done",
    none: "None recorded",
    unknownUser: "Unknown",
    undone: "Undone",
    graph: "In the graph",
    readOnly: "Read-only",
    readOnlyPublishedHint: "Already in the graph: its lines cannot be edited here.",
    readOnlyTeachHint: "Taught in the chat: there is no pattern to edit.",
    meta: (who, when) => `Saved by ${who} · ${when}`,
    undoneMeta: (who, when) => `Undone by ${who} · ${when}`,
    pattern: "What was learned",
  },
  actions: {
    edit: "Edit",
    undo: "Undo learning",
    confirmUndo: "Yes, undo",
    undoQuestion: "Stop using this pattern? You can learn it again later.",
    relearn: "Learn again",
    cancel: "Cancel",
    working: "Working…",
    editHeading: "Edit pattern",
    saveEdit: "Save edit",
  },
  notices: {
    edited: "Pattern updated.",
    undone: "Learning undone.",
    relearned: "Pattern learned again.",
  },
  paging: {
    label: "Pages",
    previous: "Previous",
    next: "Next",
    range: (from, to, total) => `${from}–${to} of ${total}`,
  },
  errors: {
    already_decided: "This pattern can no longer be changed.",
    item_gone: "This pattern is no longer in the catalog.",
    publish_pending: "The graph write is pending. Try again shortly.",
    review_unavailable: "The learning service is not available right now.",
    fallback: "That did not work. Please try again.",
  },
});

/* ---------------------------------------------------------------------------------------------------------------------- */
/*  How the conversation started (both detail popups). The context's labels are the server's; only the frame lives here.   */
/* ---------------------------------------------------------------------------------------------------------------------- */

export const SESSION_CONTEXT_TEXT = Object.freeze({
  label: "How the conversation started",
  firstQuery: "First query",
  firstQueryCaption: "The engineer's first message",
  context: "Context",
  loading: "Loading the conversation…",
  gone: "Conversation no longer available.",
  forbidden: "This conversation belongs to another engineer.",
  notConnected: "The conversation details are not connected yet.",
  failed: "Could not load the conversation details.",
  retry: "Retry",
  noFirstQuery: "No first message recorded.",
  noContext: "No context recorded.",
  copy: "Copy",
  copyLabel: "Copy the first query",
  copied: "Copied",
  copyFailed: "Could not copy",
  showMore: "Show more",
  showLess: "Show less",
  grid: Object.freeze({ engineer: "Engineer", started: "Started", messages: "Messages", status: "Status" }),
  manual: Object.freeze({
    title: "Taught manually",
    hint: "Saved from the Learning editor by an engineer, not learned from the chat on its own.",
    savedBy: "Saved by",
    savedAt: "Saved",
    category: "Category",
    caseId: "Case id",
    rootCause: "Stored root cause",
    unknown: "Unknown",
  }),
});

export const errorText = (error) => CATALOG_TEXT.errors[error && error.message] || CATALOG_TEXT.errors.fallback;

/* ---------------------------------------------------------------------------------------------------------------------- */
/*  Learning history (the Conversations view). Kinds, statuses and undo reasons come from GET /history/options; only the  */
/*  API's own parameter values and the page's copy live here.                                                              */
/* ---------------------------------------------------------------------------------------------------------------------- */

export const HISTORY_STATUS = Object.freeze({ ACTIVE: "active", UNDONE: "undone" });
export const HISTORY_ACTION = Object.freeze({ UNDO: "undo", REDO: "redo" });
export const HISTORY_RESULT = Object.freeze({ DONE: "done", NOTHING_TO_DO: "nothing_to_do" });
export const HISTORY_SOURCE = Object.freeze({ MANUAL: "manual", AUTO: "auto" });

export const HISTORY_PAGE_SIZES = Object.freeze([10, 25, 50]);
export const HISTORY_DEFAULT_PAGE_SIZE = 25;

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n)}`;

export const HISTORY_TEXT = Object.freeze({
  loading: "Loading…",
  notConnected: "The learning history is not connected yet.",
  failed: "Could not load the learning history.",
  empty: "No cases learned yet. Mark a chat Fixed and it will appear here.",
  noMatch: "No cases match these filters.",
  note: "Undo removes a case from the cause’s track record; what the case taught about individual clues stays.",
  strip: {
    label: "Cause track record",
    cases: (n) => plural(n, "case"),
    trust: (percent) => `${percent}% trust`,
  },
  filters: {
    label: "Filter learning history",
    search: "Search",
    searchHint: "Search causes",
    cause: "Cause",
    allCauses: "All causes",
    kind: "Outcome",
    allKinds: "All outcomes",
    status: "Status",
    allStatuses: "All",
    source: "Source",
    from: "From",
    to: "To",
  },
  sources: Object.freeze([
    { key: "", label: "All sources" },
    { key: HISTORY_SOURCE.MANUAL, label: "Manual root cause" },
    { key: HISTORY_SOURCE.AUTO, label: "Found by Arta" },
  ]),
  table: {
    caption: "Cases Arta learned from, newest first",
    when: "When",
    category: "Category",
    cause: "Cause",
    kind: "Outcome",
    status: "Status",
  },
  detail: {
    title: "Learned case",
    outcome: "Outcome",
    source: "Source",
    status: "Status",
    eid: "Case id",
    record: "Record",
    meta: (category, when) => [category, when].filter(Boolean).join(" · "),
  },
  badges: {
    manual: "Manual",
    manualHint: "The engineer recorded this cause by hand",
    auto: "Found by Arta",
    escalated: "Escalated",
  },
  row: {
    undo: "Undo",
    redo: "Redo",
    readOnly: "Read-only",
    readOnlyHint: "Only noted. It does not change the track record.",
    undoneAt: (when) => `on ${when}`,
    open: (label) => `Open details: ${label}`,
  },
  confirm: {
    undoTitle: "Undo this case",
    redoTitle: "Redo this case",
    loading: "Working out the effect…",
    effect: ({ label, now, after, change, casesNow, casesAfter }) =>
      `${label}: ${now}% → ${after}% (${signed(change)} pts), cases ${casesNow} → ${casesAfter}`,
    smallEffect: "Small effect: this barely moves the cause’s track record.",
    reason: "Why undo it?",
    reasonNeeded: "Choose a reason to undo this case.",
    noReasons: "The undo reasons could not be loaded. Refresh and try again.",
    confirmUndo: "Yes, undo it",
    confirmRedo: "Yes, redo it",
    cancel: "Cancel",
    retry: "Retry",
    working: "Working…",
  },
  notices: {
    undone: (label) => `Undone: 1 case taken out of ${label}.`,
    redone: (label) => `Redone: 1 case put back into ${label}.`,
    nothingToUndo: "Nothing to undo. It was already undone.",
    nothingToRedo: "Nothing to redo. It was already active.",
  },
  paging: {
    pageSize: "Cases per page",
    perPage: (n) => `${n} per page`,
    ofAll: (all) => `${all} in total`,
  },
});
